import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { PlusCircle, Trash2 } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHeader,
  TableHead,
} from "@/components/ui/table";
import {
  updateOrderSchema,
  UpdateOrderSchema,
  useOrderById,
  useUpdateOrder,
} from "@/api/orders";
import { AutocompleteAddress } from "@/components/address-auto-complete";
import { useUpdateParcels } from "@/api/parcels";
import { PhoneInput } from "@/components/ui/phone-input";
import { useGetShippingOptions } from "@/api/pricing-options";

interface EditOrderFormProps {
  orderId: string;
  onSuccess?: () => void;
}

interface PricingOption {
  speedType: string;
  estimatedDeliveryTime: { minHours: number; maxHours: number };
  price: number;
}

export function EditOrderForm({ orderId, onSuccess }: EditOrderFormProps) {
  const form = useForm<UpdateOrderSchema>({
    resolver: zodResolver(updateOrderSchema),
    defaultValues: {
      senderName: "",
      senderPhone: "",
      pickupAddress: undefined,
      recipientName: "",
      recipientPhone: "",
      deliveryAddress: undefined,
      pickupNotes: "",
      deliveryNotes: "",
      type: "STANDARD",
      parcels: [
        {
          weight: 2,
          dimensions: { length: 30, width: 30, height: 30 },
          isFragile: false,
          description: "",
          requiresCooling: false,
        },
      ],
    },
  });

  const { data: order } = useOrderById(orderId);
  const updateOrderMutation = useUpdateOrder();
  const updateParcelsMutation = useUpdateParcels(orderId, { onSuccess });
  const shippingOptionsMutation = useGetShippingOptions();

  const [pricingOptions, setPricingOptions] = useState<PricingOption[]>([]);

  const [shippingOptionsError, setShippingOptionsError] = useState<
    string | null
  >(null);

  // Update form when order data is loaded
  useEffect(() => {
    if (order) {
      // Ensure line2 values are preserved during form reset
      form.reset({
        senderName: order.senderName,
        senderPhone: order.senderPhone,
        pickupAddress: order.pickupAddress
          ? {
              ...order.pickupAddress,
              line2: order.pickupAddress.line2 || "",
            }
          : undefined,
        recipientName: order.recipientName,
        recipientPhone: order.recipientPhone,
        deliveryAddress: order.deliveryAddress
          ? {
              ...order.deliveryAddress,
              line2: order.deliveryAddress.line2 || "",
            }
          : undefined,
        type: order.type,
        parcels: order.parcels.map((parcel) => ({
          weight: parcel.weight,
          dimensions: parcel.dimensions,
          isFragile: parcel.isFragile,
          description: parcel.description,
          requiresCooling: parcel.requiresCooling,
        })),
        pickupNotes: order.pickupNotes,
        deliveryNotes: order.deliveryNotes,
      });
    }
  }, [order, form]);

  // Add this effect to watch form changes
  useEffect(() => {
    const subscription = form.watch((_, { name }) => {
      if (
        name?.includes("pickupAddress") ||
        name?.includes("deliveryAddress") ||
        name?.includes("parcels")
      ) {
        const timeoutId = setTimeout(() => {
          void fetchPricingOptions();
        }, 500);
        return () => clearTimeout(timeoutId);
      }
    });
    return () => subscription.unsubscribe();
  }, [form.watch]);

  // Add after the existing useEffect that loads order data
  useEffect(() => {
    if (order) {
      void fetchPricingOptions();
    }
  }, [order]);

  const fetchPricingOptions = async () => {
    const formData = form.getValues();

    if (
      !formData.pickupAddress?.location?.lat ||
      !formData.pickupAddress?.location?.lng ||
      !formData.pickupAddress?.postalCode ||
      !formData.deliveryAddress?.location?.lat ||
      !formData.deliveryAddress?.location?.lng ||
      !formData.deliveryAddress?.postalCode ||
      !formData.parcels?.length
    ) {
      setPricingOptions([]);
      return;
    }

    try {
      const response = await shippingOptionsMutation.mutateAsync({
        pickupAddress: {
          location: formData.pickupAddress.location,
          postalCode: formData.pickupAddress.postalCode,
        },
        deliveryAddress: {
          location: formData.deliveryAddress.location,
          postalCode: formData.deliveryAddress.postalCode,
        },
        parcels: formData.parcels.map((parcel) => ({
          weight: parcel.weight ?? 0,
          dimensions: parcel.dimensions
            ? {
                length: parcel.dimensions.length ?? 0,
                width: parcel.dimensions.width ?? 0,
                height: parcel.dimensions.height ?? 0,
              }
            : undefined,
        })),
        specialHandlingTypes: formData.parcels.flatMap((parcel) => {
          const types = [];
          if (parcel.isFragile) types.push("FRAGILE");
          if (parcel.requiresCooling) types.push("COOLING");
          return types;
        }),
        customerTierId: order?.placerCustomer?.tierId,
      });

      const { shippingOptions, reason } = response;
      setPricingOptions(shippingOptions);
      setShippingOptionsError(reason ?? null);
    } catch (error) {
      console.error("Error fetching pricing options:", error);
      setPricingOptions([]);
      setShippingOptionsError("Unable to calculate shipping options");
    }
  };

  const onSubmit = form.handleSubmit(async (values) => {
    // Preserve all address fields exactly as they are
    const cleanedValues = {
      ...values,
      pickupAddress: values.pickupAddress
        ? {
            ...values.pickupAddress,
            // Preserve line2 exactly as is, without any modification
            line2: values.pickupAddress.line2 ?? "",
          }
        : undefined,
      deliveryAddress: values.deliveryAddress
        ? {
            ...values.deliveryAddress,
            // Preserve line2 exactly as is, without any modification
            line2: values.deliveryAddress.line2 ?? "",
          }
        : undefined,
    };

    try {
      await updateOrderMutation.mutateAsync({
        orderId,
        data: cleanedValues,
      });

      await updateParcelsMutation.mutateAsync(values.parcels ?? []);
      onSuccess?.();
    } catch (error: unknown) {
      console.error("Order update failed:", error);
    }
  });

  // Add a direct click handler for the submit button
  const handleSubmitClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    void onSubmit();
  };

  return (
    <Form {...form}>
      <form
        onSubmit={onSubmit}
        className="space-y-8 max-h-[80vh] overflow-y-scroll pl-1 pr-2"
        autoComplete="off"
        data-1p-ignore
      >
        {/* Hidden field to prevent password managers from treating this as a login form */}
        <input type="hidden" name="form-type" value="order-edit" />

        <div className="grid grid-cols-2 gap-8">
          {/* Sender Column */}
          <div className="space-y-8">
            <FormField
              control={form.control}
              name="senderName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sender Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Sender Name"
                      {...field}
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="senderPhone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sender Phone</FormLabel>
                  <FormControl>
                    <PhoneInput
                      placeholder="Sender Phone"
                      {...field}
                      value={field.value}
                      onChange={(value) => field.onChange(value || "")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="w-full">
              <FormLabel>Pickup Address</FormLabel>
              <div className="mt-2 relative z-50">
                <FormField
                  control={form.control}
                  name="pickupAddress"
                  render={({ field }) => (
                    <AutocompleteAddress
                      onAddressChange={(address) => {
                        if (address) {
                          // For line2 changes, preserve existing location
                          const existingLocation = field.value?.location ?? {
                            lat: 0,
                            lng: 0,
                          };

                          // Preserve line2 exactly as is
                          const line2 = address.addressComponents.line2;

                          const updatedAddress = {
                            line1: address.addressComponents.line1,
                            line2: line2,
                            city: address.addressComponents.city,
                            province: address.addressComponents.state,
                            postalCode: address.addressComponents.postalCode,
                            country: address.addressComponents.country,
                            location:
                              address.location.lat === 0
                                ? existingLocation
                                : address.location,
                          };

                          form.setValue("pickupAddress", updatedAddress, {
                            shouldValidate: address.location.lat !== 0,
                          });
                        }
                      }}
                      onLine2Change={(value) => {
                        // Get current pickup address
                        const currentAddress = form.getValues("pickupAddress");
                        if (currentAddress) {
                          // Update only the line2 field
                          form.setValue(
                            "pickupAddress",
                            {
                              ...currentAddress,
                              line2: value,
                            },
                            {
                              shouldValidate: false,
                            }
                          );
                        }
                      }}
                      mode="edit"
                      placeholder="Search pickup address"
                      initialAddress={{
                        formattedString: field.value
                          ? `${field.value.line1}${field.value.line2 ? ", " + field.value.line2 : ""}`
                          : "",
                        components: {
                          line1: field.value?.line1 ?? "",
                          line2: field.value?.line2 ?? "",
                          city: field.value?.city ?? "",
                          state: field.value?.province ?? "",
                          postalCode: field.value?.postalCode ?? "",
                          country: field.value?.country ?? "",
                        },
                        location: field.value?.location,
                      }}
                      ui={{
                        alwaysShowForm: true,
                      }}
                    />
                  )}
                />
              </div>
            </div>

            <FormField
              control={form.control}
              name="pickupNotes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pickup Notes</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Add any special instructions for pickup"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Recipient Column */}
          <div className="space-y-8">
            <FormField
              control={form.control}
              name="recipientName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Recipient Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Recipient Name"
                      {...field}
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="recipientPhone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Recipient Phone</FormLabel>
                  <FormControl>
                    <PhoneInput
                      placeholder="Recipient Phone"
                      {...field}
                      value={field.value}
                      onChange={(value) => field.onChange(value || "")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="w-full">
              <FormLabel>Delivery Address</FormLabel>
              <div className="mt-2 relative z-50">
                <FormField
                  control={form.control}
                  name="deliveryAddress"
                  render={({ field }) => (
                    <AutocompleteAddress
                      onAddressChange={(address) => {
                        if (address) {
                          // For line2 changes, preserve existing location
                          const existingLocation = field.value?.location ?? {
                            lat: 0,
                            lng: 0,
                          };

                          // Preserve line2 exactly as is
                          const line2 = address.addressComponents.line2;

                          const updatedAddress = {
                            line1: address.addressComponents.line1,
                            line2: line2,
                            city: address.addressComponents.city,
                            province: address.addressComponents.state,
                            postalCode: address.addressComponents.postalCode,
                            country: address.addressComponents.country,
                            location:
                              address.location.lat === 0
                                ? existingLocation
                                : address.location,
                          };

                          form.setValue("deliveryAddress", updatedAddress, {
                            shouldValidate: address.location.lat !== 0,
                          });
                        }
                      }}
                      onLine2Change={(value) => {
                        // Get current delivery address
                        const currentAddress =
                          form.getValues("deliveryAddress");
                        if (currentAddress) {
                          // Update only the line2 field
                          form.setValue(
                            "deliveryAddress",
                            {
                              ...currentAddress,
                              line2: value,
                            },
                            {
                              shouldValidate: false,
                            }
                          );
                        }
                      }}
                      mode="edit"
                      placeholder="Search delivery address"
                      initialAddress={{
                        formattedString: field.value
                          ? `${field.value.line1}${field.value.line2 ? ", " + field.value.line2 : ""}`
                          : "",
                        components: {
                          line1: field.value?.line1 ?? "",
                          line2: field.value?.line2 ?? "",
                          city: field.value?.city ?? "",
                          state: field.value?.province ?? "",
                          postalCode: field.value?.postalCode ?? "",
                          country: field.value?.country ?? "",
                        },
                        location: field.value?.location,
                      }}
                      ui={{
                        alwaysShowForm: true,
                      }}
                    />
                  )}
                />
              </div>
            </div>

            <FormField
              control={form.control}
              name="deliveryNotes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Delivery Notes</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Add any special instructions for delivery"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Parcels</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Dimensions (L×W×H cm)</TableHead>
                  <TableHead>Weight (kg)</TableHead>
                  <TableHead>Special Handling</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {form.watch("parcels")?.map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Input
                          type="number"
                          placeholder="L"
                          {...form.register(
                            `parcels.${index}.dimensions.length`,
                            { valueAsNumber: true }
                          )}
                          className="w-20"
                        />
                        <Input
                          type="number"
                          placeholder="W"
                          {...form.register(
                            `parcels.${index}.dimensions.width`,
                            { valueAsNumber: true }
                          )}
                          className="w-20"
                        />
                        <Input
                          type="number"
                          placeholder="H"
                          {...form.register(
                            `parcels.${index}.dimensions.height`,
                            { valueAsNumber: true }
                          )}
                          className="w-20"
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        placeholder="Weight"
                        {...form.register(`parcels.${index}.weight`, {
                          valueAsNumber: true,
                        })}
                        className="w-24"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Checkbox
                          checked={form.watch(`parcels.${index}.isFragile`)}
                          onCheckedChange={(checked) =>
                            form.setValue(
                              `parcels.${index}.isFragile`,
                              !!checked
                            )
                          }
                        />
                        <span className="text-sm">Fragile</span>
                        <Checkbox
                          checked={form.watch(
                            `parcels.${index}.requiresCooling`
                          )}
                          onCheckedChange={(checked) =>
                            form.setValue(
                              `parcels.${index}.requiresCooling`,
                              !!checked
                            )
                          }
                        />
                        <span className="text-sm">Cooling</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {(form.watch("parcels") ?? []).length > 1 && (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={() => {
                            const parcels = form.getValues("parcels");
                            form.setValue(
                              "parcels",
                              parcels?.filter((_, i) => i !== index)
                            );
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
          <CardFooter className="justify-center border-t p-4">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="gap-1"
              onClick={() => {
                const parcels = form.getValues("parcels") ?? [];
                form.setValue("parcels", [
                  ...parcels,
                  {
                    weight: 2,
                    dimensions: { length: 30, width: 30, height: 30 },
                    isFragile: false,
                    description: "",
                    requiresCooling: false,
                  },
                ]);
              }}
            >
              <PlusCircle className="h-3.5 w-3.5" />
              Add Parcel
            </Button>
          </CardFooter>
        </Card>

        {/* Pricing Options Display */}
        {(pricingOptions || shippingOptionsError) && (
          <Card>
            <CardHeader>
              <CardTitle>Delivery Options</CardTitle>
            </CardHeader>
            <CardContent>
              {pricingOptions.length > 0 ? (
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <div className="space-y-4">
                      {pricingOptions.map((option) => (
                        <div
                          key={option.speedType}
                          className={`p-6 rounded-xl max-w-[500px] border-2 cursor-pointer transition-all ${
                            field.value === option.speedType
                              ? "border-blue-500 bg-blue-50/50"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                          onClick={() =>
                            field.onChange(
                              option.speedType as
                                | "STANDARD"
                                | "EXPRESS"
                                | "SCHEDULED"
                                | "SAME_DAY"
                            )
                          }
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-medium text-lg">
                                {option.speedType.replace("_", " ")}
                              </h4>
                              <p className="text-sm text-gray-500 mt-1">
                                Estimated Delivery Time:{" "}
                                {typeof option.estimatedDeliveryTime ===
                                "object"
                                  ? `${option.estimatedDeliveryTime.minHours}-${option.estimatedDeliveryTime.maxHours} hours`
                                  : option.estimatedDeliveryTime}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium">
                                ${option.price.toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                />
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-500">
                    {shippingOptionsError ?? (
                      <>
                        {form.watch("pickupAddress") &&
                        form.watch("deliveryAddress")
                          ? "Calculating delivery options..."
                          : "Complete pickup and delivery addresses to view options"}
                      </>
                    )}
                  </p>
                  {shippingOptionsError && (
                    <p className="text-sm text-amber-600 mt-2">
                      {shippingOptionsError}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="flex justify-end">
          <Button
            type="submit"
            name="submit-order"
            value="update"
            aria-label="Update Order"
            onClick={handleSubmitClick}
          >
            Update Order
          </Button>
        </div>
      </form>
    </Form>
  );
}
